import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { DialogTitle, FormHelperText } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { nanoid } from 'nanoid';
import {
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { CLAIM_REJECT_OVERRIDE_OTHER } from '@constants/app';
import ClaimsRejectOverrideReasons from '../../../ClaimsRejectOverrideReasons';

function RejectOverrideClaimModal({
  open,
  onClose,
  claimId,
  serviceJobId,
  setOpenModal,
  formRef,
  preProcessClaimRequest,
}) {
  const { formatMessage } = useLocalization();
  const validationschema = yup.object().shape({
    rejectReasonCode: yup.object().required(
      formatMessage({
        id: 'REJECT_REASON_IS_REQUIRED',
        defaultMessage: 'Reject reason is required',
      }),
    ),
    rejectReasonOtherText: yup.string().when('rejectReasonCode', {
      is: val => val?.value === CLAIM_REJECT_OVERRIDE_OTHER,
      then: schema =>
        schema.required(
          formatMessage({
            id: 'PLEASE_SPECIFY_A_REASON',
            defaultMessage: 'Please specify a reason',
          }),
        ),
      otherwise: schema => schema.notRequired(),
    }),
  });
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put } = useDataProvider(provider);

  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: yupResolver(validationschema),
  });
  const { handleSubmit, control } = methods;

  const buttons = [
    {
      children: formatMessage({
        id: 'REJECT_OVERRIDE_CLAIM',
        defaultMessage: 'Reject Override Claim',
      }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => setOpenModal(false),
      variant: 'contained',
      color: 'secondary',
    },
  ];

  const preProcessRequestData = data => {
    const claimDialogFormData = preProcessClaimRequest(
      formRef.current.getValues(),
      true,
    );
    const processedData = {};
    const { rejectReasonCode, rejectReasonOtherText } = data;
    processedData.rejectCode = rejectReasonCode.value;
    processedData.rejectText =
      rejectReasonCode.value === CLAIM_REJECT_OVERRIDE_OTHER
        ? rejectReasonOtherText
        : rejectReasonCode.label;
    processedData.recordVersion = claimDialogFormData?.recordVersion;
    return processedData;
  };

  const submitReject = async data => {
    setLoading(true);
    const body = preProcessRequestData(data);
    const response = await put(
      `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${claimId}/reject-override`,
      body,
    );
    setLoading(false);
    if (response.ok) {
      snackbar.showMessage({
        type: 'success',
        data: (
          <FormattedMessage
            id="CLAIM_REJECT_OVERRIDE_SUCCESSFUL"
            defaultMessage="Claim reject override successful"
          />
        ),
      });
      onClose(nanoid());
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
  };

  const onFormSubmit = data => {
    submitReject(data);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 400,
        bgcolor: 'background.paper',
      }}>
      <Dialog
        id="rejectDialog"
        sx={{ '& .MuiDialog-paper': { width: '80%' } }}
        open={open}>
        {!loading && (
          <DialogTitle style={{ padding: '8px 20px 30px', fontSize: '18px' }}>
            <FormattedMessage
              id="REJECT_OVERRIDE_CLAIM"
              defaultMessage="Reject Override Claim"
            />
          </DialogTitle>
        )}
        {loading ? (
          <DialogContent>
            <LoadingEvent>
              <FormattedMessage
                id="PROCESSING_REJECT_OVERRIDE_CLAIM"
                defaultMessage="Processing Reject Override Claim..."
              />
            </LoadingEvent>
          </DialogContent>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <DialogContent>
                <Grid2 container spacing={2} flexDirection="column">
                  <Grid2>
                    <Controller
                      name="rejectReasonCode"
                      control={control}
                      render={({ field, fieldState: { error: fsError } }) => {
                        return (
                          <FieldWrapperVertical
                            field={{
                              name: field?.name,
                              label: {
                                localeId: 'PLEASE_ADD_REJECT_REASON',
                                defaultMessage: 'Please add reject reason',
                              },
                            }}>
                            <ClaimsRejectOverrideReasons
                              control={control}
                              {...field}
                              field={{
                                dependencies: {
                                  other: {
                                    name: 'rejectReasonOtherText',
                                    placeholder: {
                                      id: 'PLEASE_ADD_REJECT_REASON',
                                      defaultMessage:
                                        'Please add reject reason',
                                    },
                                  },
                                },
                              }}
                            />
                            {fsError?.message ? (
                              <FormHelperText
                                sx={{ typography: 'body2' }}
                                error={Boolean(fsError?.message)}>
                                {fsError?.message}
                              </FormHelperText>
                            ) : null}
                          </FieldWrapperVertical>
                        );
                      }}
                    />
                  </Grid2>
                </Grid2>
              </DialogContent>
              {!loading ? (
                <DialogActions
                  display="flex"
                  sx={{ justifyContent: 'center', my: 2 }}>
                  {buttons.map(btn => {
                    const { children, ...rest } = btn;
                    return (
                      <Button key={children} disabled={loading} {...rest}>
                        {children}
                      </Button>
                    );
                  })}
                </DialogActions>
              ) : null}
            </form>
          </FormProvider>
        )}
      </Dialog>
    </Box>
  );
}

export default RejectOverrideClaimModal;
